import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';
import { FaArrowRight } from 'react-icons/fa';
import './AdminCommunityEdit.css';

const DEFAULT_FORM = {
  section_tag: 'انضمي لمجتمعنا',
  title_prefix: 'انضمي لأكثر من',
  title_highlight: '300+ باحثة متميزة',
  title_suffix: 'حققن النجاح وتجاوزن العقبات!',
  description_text:
    'هذه فرصتك لتكوني جزءًا من مجتمع التميز البحثي. احصلي على نصائح حصرية، تحديثات دورية، وخصومات خاصة للمشتركات في القائمة البريدية.',
  name_placeholder: 'الاسم الكريم',
  email_placeholder: 'البريد الإلكتروني',
  button_text: 'اشتركي الآن',
  partner1_text: 'جامعة الملك سعود',
  partner2_text: 'المكتبة الرقمية',
  partner3_text: 'البحث العلمي',
  partner4_text: 'دراسات عليا',
  partner5_text: 'مجلة العلوم',
};

const AdminCommunityEdit = () => {
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  const empty = useMemo(() => DEFAULT_FORM, []);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/admin/community`, { headers: getAuthHeaders() })
      .then((res) => {
        const c = res.data?.community;
        if (!c) return setForm(empty);
        setForm({
          section_tag: c.section_tag ?? empty.section_tag,
          title_prefix: c.title_prefix ?? empty.title_prefix,
          title_highlight: c.title_highlight ?? empty.title_highlight,
          title_suffix: c.title_suffix ?? empty.title_suffix,
          description_text: c.description_text ?? empty.description_text,
          name_placeholder: c.name_placeholder ?? empty.name_placeholder,
          email_placeholder: c.email_placeholder ?? empty.email_placeholder,
          button_text: c.button_text ?? empty.button_text,
          partner1_text: c.partner1_text ?? empty.partner1_text,
          partner2_text: c.partner2_text ?? empty.partner2_text,
          partner3_text: c.partner3_text ?? empty.partner3_text,
          partner4_text: c.partner4_text ?? empty.partner4_text,
          partner5_text: c.partner5_text ?? empty.partner5_text,
        });
      })
      .catch((err) => {
        console.error('Failed to load community:', err);
        setForm(empty);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/admin/community`, form, {
        headers: getAuthHeaders(),
      });
      alert('تم حفظ قسم المجتمع بنجاح');
      navigate('/admin');
    } catch (err) {
      alert(err.response?.data?.error || 'فشل حفظ قسم المجتمع');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-loading">جاري التحميل...</div>;

  return (
    <div className="admin-community-edit" dir="rtl">
      <div className="admin-community-edit-header">
        <Link to="/admin" className="back-link">
          <FaArrowRight /> العودة للصفحة الرئيسية
        </Link>
        <h1 className="admin-page-title">تعديل قسم المجتمع</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-community-form">
        <div className="form-row">
          <label>Section Tag</label>
          <input
            className="form-input"
            value={form.section_tag}
            onChange={(e) => handleChange('section_tag', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>عنوان (Prefix)</label>
          <input
            className="form-input"
            value={form.title_prefix}
            onChange={(e) => handleChange('title_prefix', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>عنوان (Highlight)</label>
          <input
            className="form-input"
            value={form.title_highlight}
            onChange={(e) => handleChange('title_highlight', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>عنوان (Suffix)</label>
          <input
            className="form-input"
            value={form.title_suffix}
            onChange={(e) => handleChange('title_suffix', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>الوصف</label>
          <textarea
            className="form-input"
            rows={4}
            value={form.description_text}
            onChange={(e) => handleChange('description_text', e.target.value)}
          />
        </div>

        <div className="form-row inline-row">
          <div>
            <label>Placeholder الاسم</label>
            <input
              className="form-input short"
              value={form.name_placeholder}
              onChange={(e) => handleChange('name_placeholder', e.target.value)}
            />
          </div>
          <div>
            <label>Placeholder البريد</label>
            <input
              className="form-input short"
              value={form.email_placeholder}
              onChange={(e) => handleChange('email_placeholder', e.target.value)}
            />
          </div>
          <div>
            <label>نص الزر</label>
            <input
              className="form-input short"
              value={form.button_text}
              onChange={(e) => handleChange('button_text', e.target.value)}
            />
          </div>
        </div>

        <div className="admin-community-partners">
          <h2 className="admin-community-subtitle">شعارات/شركاء (نصوص)</h2>
          <div className="form-row">
            <label>Partner 1</label>
            <input
              className="form-input"
              value={form.partner1_text}
              onChange={(e) => handleChange('partner1_text', e.target.value)}
            />
          </div>
          <div className="form-row">
            <label>Partner 2</label>
            <input
              className="form-input"
              value={form.partner2_text}
              onChange={(e) => handleChange('partner2_text', e.target.value)}
            />
          </div>
          <div className="form-row">
            <label>Partner 3</label>
            <input
              className="form-input"
              value={form.partner3_text}
              onChange={(e) => handleChange('partner3_text', e.target.value)}
            />
          </div>
          <div className="form-row">
            <label>Partner 4</label>
            <input
              className="form-input"
              value={form.partner4_text}
              onChange={(e) => handleChange('partner4_text', e.target.value)}
            />
          </div>
          <div className="form-row">
            <label>Partner 5</label>
            <input
              className="form-input"
              value={form.partner5_text}
              onChange={(e) => handleChange('partner5_text', e.target.value)}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="admin-btn-primary" disabled={saving}>
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </button>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={() => navigate('/admin')}
            disabled={saving}
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCommunityEdit;

